/**
 * Freshdesk Status/Health tab. The connection pill, domain, checked-at and error
 * all come from the cached `freshdesk` run summary (no live FD call on load); the
 * Data panel shows each synced data type's count + freshness with a link to its
 * tab; the bottom feed lists recent runs across ALL Freshdesk jobs (health + KB
 * + future syncs). "Run health check" is the only button that kicks live FD
 * traffic from this tab.
 */
import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import { Link as RouterLink } from 'react-router-dom'

import { useDataFetching } from '../../../../../hooks/useDataFetching'
import { useNotify } from '../../../../../components/shared/ToastProvider'
import {
  FreshdeskSectionSummary,
  integrationsApi,
  IntegrationRun,
  IntegrationStatus,
} from '../../../../../services/admin/integrationsApi'
import IntegrationRunsTable from '../shared/IntegrationRunsTable'
import TableResultsToolbar from '../../../../../components/shared/TableResultsToolbar'
import { relativeTime } from '../shared/relativeTime'
import { anyRunActive, usePollWhileRunning } from '../shared/usePollWhileRunning'

const KEY = 'freshdesk'
const BASE = '/admin/system/integrations/freshdesk'

// Friendly labels for the combined Freshdesk run feed's "Job" column. Lives here
// (the only showJob consumer), not in the shared table, which stays
// integration-agnostic. Unmapped keys fall back to the raw key.
const FRESHDESK_JOB_LABELS: Record<string, string> = {
  freshdesk: 'Health check',
  freshdesk_solutions: 'Solutions sync',
  freshdesk_companies: 'Companies sync',
  freshdesk_tickets: 'Ticket counts',
}

// Leads the Connection card: a big icon + word so the health state reads at a
// glance, ahead of the domain + details below.
const ConnectionHero: React.FC<{ ok?: boolean | null }> = ({ ok }) => {
  if (ok == null) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <HelpOutlineIcon sx={{ color: 'text.disabled' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Never checked
        </Typography>
      </Stack>
    )
  }
  const Icon = ok ? CheckCircleIcon : ErrorIcon
  const color = ok ? 'success.main' : 'error.main'
  const label = ok ? 'Connected' : 'Connection failed'
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Icon sx={{ color }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>
        {label}
      </Typography>
    </Stack>
  )
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Stack>
)

const sectionDetail = (item: FreshdeskSectionSummary): string => {
  if (!item.available) return 'Coming soon'
  const counts = item.entities.map((e) => `${e.count} ${e.label.toLowerCase()}`)
  const prefix = counts.length ? `${counts.join(' · ')} · ` : ''
  return `${prefix}synced ${relativeTime(item.last_synced_at)}`
}

// Icon per Freshdesk data section, keyed by the summary `key`.
const SECTION_ICONS: Record<string, React.ReactNode> = {
  solutions: <LayersOutlinedIcon />,
  companies: <BusinessOutlinedIcon />,
  contacts: <PersonOutlineIcon />,
  tickets: <ConfirmationNumberOutlinedIcon />,
}

const DataRow: React.FC<{ item: FreshdeskSectionSummary }> = ({ item }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          color: item.available ? 'primary.main' : 'text.disabled',
          display: 'flex',
        }}
      >
        {SECTION_ICONS[item.key] ?? null}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {item.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sectionDetail(item)}
        </Typography>
      </Box>
    </Stack>
    {item.available ? (
      <Button size="small" component={RouterLink} to={`${BASE}/${item.tab}`}>
        View
      </Button>
    ) : (
      <Chip size="small" variant="outlined" label="Planned" />
    )}
  </Stack>
)

const FreshdeskStatus: React.FC = () => {
  const notify = useNotify()
  const [triggering, setTriggering] = useState(false)
  const [runsPage, setRunsPage] = useState(1)
  const [runsPerPage, setRunsPerPage] = useState(25)

  const {
    data: status,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useDataFetching<IntegrationStatus>({
    fetchFn: () => integrationsApi.getIntegrationStatus(KEY),
    dependencies: [],
  })

  const { data: summary } = useDataFetching<FreshdeskSectionSummary[]>({
    fetchFn: () => integrationsApi.getFreshdeskSummary(),
    dependencies: [],
  })

  const { data: runs, refetch: refetchRuns } = useDataFetching<IntegrationRun[]>({
    fetchFn: () => integrationsApi.getFreshdeskRuns(50),
    dependencies: [],
  })

  // Poll status + runs while ANY Freshdesk job is in flight (the health probe or
  // a Solutions / Companies sync from another tab) so the combined feed refreshes
  // live, then settle once done.
  usePollWhileRunning(runs, () => {
    refetchStatus()
    refetchRuns()
  })
  // ...but only a running *health* run gates the health-check button - the other
  // syncs are different run keys the backend would run a health check alongside.
  const healthRunning = anyRunActive(runs?.filter((r) => r.integration_key === KEY))

  const health = status?.health ?? null
  const allRuns = runs ?? []
  const pagedRuns = allRuns.slice(
    (runsPage - 1) * runsPerPage,
    runsPage * runsPerPage,
  )

  const runHealthCheck = async () => {
    setTriggering(true)
    try {
      await integrationsApi.triggerIntegrationRun(KEY)
      notify.showSuccess('Health check started')
      // The run completes in the background; refetch shortly after.
      setTimeout(() => {
        refetchStatus()
        refetchRuns()
      }, 1500)
    } catch (err: any) {
      if (err?.response?.status === 409) {
        notify.showInfo('A health check is already running')
      } else {
        notify.showError('Failed to start health check')
      }
    } finally {
      setTriggering(false)
    }
  }

  if (statusLoading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    // One Grid container (not a Grid nested in a Stack) so the two top cards
    // share the same left/right bounds as the full-width Recent runs card below
    // - the nested-grid-in-Stack layout left the top row visually off-centre.
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Connection</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  triggering || healthRunning ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={runHealthCheck}
                disabled={triggering || healthRunning}
              >
                Run health check
              </Button>
            </Stack>

            <ConnectionHero ok={health?.ok} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {status?.domain || '-'}
            </Typography>

            <Stack spacing={1}>
              <Field label="Last checked">{relativeTime(health?.checked_at)}</Field>
              <Field label="Scheduled sync">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={status?.enabled ? 'success' : 'default'}
                    label={status?.enabled ? 'Enabled' : 'Disabled'}
                  />
                  {!status?.enabled && (
                    <Typography variant="caption" color="text.secondary">
                      Manual sync only
                    </Typography>
                  )}
                </Stack>
              </Field>
              <Field label="Connection">Read-only</Field>
              {health?.error && (
                <Field label="Error">
                  <Typography variant="body2" color="error">
                    {health.error}
                  </Typography>
                </Field>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Data
            </Typography>
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              {(summary ?? []).map((item) => (
                <DataRow key={item.key} item={item} />
              ))}
            </Stack>
          </Paper>
        </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 1 }}
          >
            <Typography variant="h6">Recent runs</Typography>
            <TableResultsToolbar
              page={runsPage}
              perPage={runsPerPage}
              total={allRuns.length}
              onPageChange={setRunsPage}
              onPerPageChange={(n) => {
                setRunsPerPage(n)
                setRunsPage(1)
              }}
            />
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <IntegrationRunsTable
            runs={pagedRuns}
            showJob
            jobLabels={FRESHDESK_JOB_LABELS}
          />
        </Paper>
      </Grid>
    </Grid>
  )
}

export default FreshdeskStatus
